import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDroppable } from '@dnd-kit/core';
import { alpha, Button, Fade, Paper, Typography } from '@material-ui/core';
import { ArrowDownward } from '@material-ui/icons';
import { CardTitle, StyledStatusLabel } from '@sierralabs/core-ui';
import { isKanbanBoardItemEqual } from './utils/isKanbanBoardItemEqual';
import { DraggableKanbanBoardCard } from './KanbanBoardCard';
import { useKanbanBoardContext } from './KanbanBoardContext';
const StyledDiv = styled.div `
  position: relative;
  display: flex;
  margin: 12px 12px 0 12px;
  width: 100%;
  background: #f7f8fc;
  .board-column {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0;
    overflow: 0;
    min-width: 320px;
    min-height: 48px;
    border-radius: 4px 4px 0 0;
    &:not(:last-child) {
      margin-right: 12px;
    }
  }
  .card-title {
    width: 100%;
    padding: 12px 16px;
    display: inline-block;
    overflow-x: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    border-bottom: 1px solid #ddd;
    transition: background 0.2s cubic-bezier(0.075, 0.82, 0.165, 1),
      color 0.2s cubic-bezier(0.075, 0.82, 0.165, 1);
  }
  .card-container {
    flex: 1;
    padding: 10px;
    overflow-y: auto;
    background: linear-gradient(90deg, #e4f2fd 0%, #d3e8f4 99.94%);
    transition: background 0.2s cubic-bezier(0.075, 0.82, 0.165, 1);
    &.droppable:before {
      z-index: 1;
      outline-color: ${props => props.theme.palette.grey[500]};
    }
    &.over:before {
      outline-color: ${props => props.theme.palette.primary.main};
    }
    &:before {
      content: '';
      position: absolute;
      left: 0;
      /* for padding */
      margin-top: -10px;
      outline-offset: -3px;
      width: 100%;
      height: calc(100% - 56.8px);
      outline: 3px dashed ${props => alpha(props.theme.palette.grey[500], 0)};
      transition: outline-color 0.2s cubic-bezier(0.075, 0.82, 0.165, 1);
    }
  }
  .card-overlay {
    position: absolute;
    top: calc(56.8px + 3px);
    left: 3px;
    width: calc(100% - 6px);
    height: calc(100% - 56.8px - 3px);
    background: ${props => alpha(props.theme.palette.primary.light, 0.8)};
    padding: 20px 10px 10px;
    display: flex;
    align-items: center;
    flex-direction: column;
    .overlay-title {
      margin: 4px 0;
      font-weight: 500;
      text-shadow: 1px 1px 2px white;
    }
  }
`;
export const KanbanBoardRow = () => {
    const styleItemsRef = useRef([]);
    const { items, columns } = useKanbanBoardContext();
    return (React.createElement(StyledDiv, { className: 'board-row' }, columns.map(column => (React.createElement(BoardColumn, { key: column.id, column: column, items: items.filter(item => item.columnId === column.id), getItemStyle: item => {
            const style = styleItemsRef.current.find(style => {
                return isKanbanBoardItemEqual(style, item);
            });
            return style === null || style === void 0 ? void 0 : style.style;
        }, onSize: (item, height) => {
            const index = styleItemsRef.current.findIndex(style => {
                return isKanbanBoardItemEqual(style, item);
            });
            if (index > -1 && typeof height === 'undefined') {
                styleItemsRef.current.splice(index, 1);
                return;
            }
            const style = {
                id: item.id,
                taskId: item.taskId,
                columnId: item.columnId,
                style: { minHeight: height }
            };
            if (index > -1) {
                styleItemsRef.current[index] = style;
            }
            else {
                styleItemsRef.current.push(style);
            }
        } })))));
};
export default KanbanBoardRow;
// default number of cards to display per column in row
const pageSize = 50;
const BoardColumn = props => {
    const { items, column, onSize, getItemStyle } = props;
    const [limit, setLimit] = useState(pageSize);
    const [eventName, setEventName] = useState();
    const { drag, findEvent } = useKanbanBoardContext();
    const { name, color: stateColor } = column.state || {};
    const title = column.name || name;
    const color = stateColor && typeof stateColor === 'object' ? stateColor : undefined;
    const next = useMemo(() => {
        return Math.min(limit + pageSize, items.length);
    }, [items.length, limit]);
    // if event transitions include the state name, enable drop
    const event = useMemo(() => {
        return drag && name ? findEvent(drag.item.status, name) : undefined;
    }, [drag, name, findEvent]);
    const canDrop = !!event;
    const { isOver, setNodeRef } = useDroppable({
        id: column.id,
        disabled: !canDrop
    });
    const classes = ['card-container'];
    isOver && classes.push('over');
    canDrop && classes.push('droppable');
    // for styling effect only, persist event name even after it gets unset
    useEffect(() => {
        event && setEventName(event);
    }, [event]);
    return (React.createElement(Paper, { className: 'board-column' },
        React.createElement(CardTitle, { style: canDrop ? color : undefined }, !canDrop ? (title) : (React.createElement(React.Fragment, null,
            React.createElement("strong", null, eventName || '?'),
            " (",
            title,
            ")"))),
        React.createElement("div", { ref: setNodeRef, className: classes.join(' ') },
            items.slice(0, limit).map((item, index) => (React.createElement(DraggableKanbanBoardCard, { item: item, column: column, style: getItemStyle === null || getItemStyle === void 0 ? void 0 : getItemStyle(item), key: `item-${column.id}-${item.id}-${item.taskId}-${index}`, onSize: height => onSize === null || onSize === void 0 ? void 0 : onSize(item, height) }))),
            limit < items.length && (React.createElement("div", { className: 'load-more' },
                React.createElement(Button, { fullWidth: true, color: 'primary', onClick: () => setLimit(limit => limit + pageSize) },
                    "Show ",
                    next,
                    " of ",
                    items.length))),
            React.createElement(Fade, { in: isOver },
                React.createElement("div", { className: 'card-overlay' },
                    React.createElement(ArrowDownward, null),
                    React.createElement(Typography, { variant: 'subtitle1', className: 'overlay-title' }, "Drop item here"),
                    React.createElement(StyledStatusLabel, { style: color, className: 'status-label' }, eventName || 'Transition Item'))))));
};
//# sourceMappingURL=KanbanBoardRow.js.map